import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Calendar,
  MapPin,
  Globe,
  Info,
  AlertCircle,
  FileText,
  Users,
  BarChart,
  Lock,
  Building,
  ShoppingCart,
  NotebookPen,
  Folder,
  Compass,
  ReceiptIndianRupee,
  Check,
  UserRoundKey,
  ExternalLink,
  Loader2,
} from "lucide-react";
import EpfoTab from "../../components/EpfoTab";
import DirectorDetails from "./DirectorDetails";
import { RiskGradeSection } from "../../components/onepager/sections/RiskGradeSection";
import { CompanyOverview } from "../../components/company-overview";
import { memo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import apiService from "../../services/apiService";
import { toast } from "react-toastify";
import { getLocalStorageItem } from "../../utils";
import { Edit, Download, LockKeyholeOpen, X } from "lucide-react";
import LegalTab from "../../components/LegalTab";
import FinancialTab from "../../components/FinancialTab";
import RelatedParty from "../../components/RelatedParty";
import ShareholdingPattern from "../../components/ShareholdingPattern";
import SecuritiesAllotment from "../../components/SecuritiesAllotment";
import DebtProfile from "./DebtProfile";
import LLPContributionDetails from "../../components/LLPContributionDetails";
import { useCustomerStore } from "../../stores/customerStore";
import { CreditCardOutlined, PieChartOutlined } from "@ant-design/icons";
import {
  TooltipProvider,
} from "../../components/ui/tooltip";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@radix-ui/react-collapsible";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useSidebar } from "../../context/SidebarContext";
import { CustomerData, onePageModelData } from "../../types/CustomerInterface";
import { GSTComplianceSection } from "../../components/GSTComplianceSection";
import OnePagerModal from "../../components/onepager/OnePagerModal";
import DataUpdatePopup from "../../components/DataUpdatePopup";
import type { FinancialData } from "../../types/FinancialInterface";
import RentalCalculatorLayout from "../../components/RentalCalculator/RentalCalculatorLayout";
import CreditNotes from "./CreditNotes";
import DataRoom from "../../components/dataRoom/DataRoom";
import FinancialDashboard from "../../components/FinancialDashboard";
import { FinancialDataMatric } from "../../types/FinancialInterface";
import {
  calculateFinancialRatios,
  calculateLLPFinancialRatios,
} from "../../utils/financialRatios";
import { getDefaultRecords } from "../../utils/financialTop3consolidate";
import PeerComparison from "../../components/PeerComparison";
import Login from "../login/login";
import PurchaseOrders from "../purchaseOrder/PurchaseOrders";
import PODetail from "../purchaseOrder/PODetail";
import { Button } from "../../components/ui/button";
import { CustomerAddressManagementDialog } from "../../components/customers/CustomerAddressManagementDialog";
import { fetchCustomerAddresses, getAddressTypeLabel } from "../../lib/customer-addresses";
import RentalSchedulesPage from "../rental-schedules/RentalSchedulesPage";
import CustomerDocumentsSheet from "../../components/documents/CustomerDocumentsSheet";
import RequirementDetail from "../../pages/manage-login/RequirementDetail";
import TeamMembersContent from "../../components/customerteams/managecustomerteams";
import { useCustomerWorkflow } from "../../context/CustomerWorkflowContext";
interface companies {
  id: string;
  name: string;
  cin: string;
}
interface ApiResponse<T> {
  data: T;
  actionButton?: string;
  company?: any;
  gstEpfo?: {
    gst_details: Array<any>;
    establishments_registered_with_epfo: Array<any>;
  };
  message?: string;
  status?: number;
}

interface CustomerDetails {
  brandName?: string;
  domain?: string;
  customerImage?: string;
  customerBannerImage?: string;
  socialBanners?: string[];
  linkedinUrl?: string;
  facebookUrl?: string;
  instagramUrl?: string;
  youtubeUrl?: string;
  twitterUrl?: string;
  entity_type?: string;
  industry?: string;
  creditRating?: string;
  turnoverYear?: string;
  networthYear?: string;
  turnoverValue?: string;
  networthValue?: string;
  creditRatingAgency?: string;
  entityName?: string;
  /** Raw Apollo enrich JSON from getCustomerBasicProfile (`company.apolloOrganization`). */
  apolloOrganization?: unknown;
}

interface AdminUserList {
  id: number;
  name: string;
}

interface CustomerImageUploadResponse {
  data?: {
    fileUrl?: string;
    customerImage?: string;
    customerBannerImage?: string;
  };
  fileUrl?: string;
  customerImage?: string;
  customerBannerImage?: string;
  message?: string;
}

const extractUploadedImageUrl = (
  response: CustomerImageUploadResponse
): string | undefined =>
  response?.data?.fileUrl ??
  response?.data?.customerImage ??
  response?.fileUrl ??
  response?.customerImage;

const withBannerImageSize = (url?: string | null) => {
  const trimmed = (url ?? "").trim();
  if (!trimmed) return "";
  if (/[?&]size=\d+/i.test(trimmed)) return trimmed;
  return trimmed.includes("?") ? `${trimmed}` : `${trimmed}?`;
};

const normalizeSocialBanners = (banners: unknown): string[] => {
  if (!Array.isArray(banners)) return [];
  return banners
    .map((banner) => withBannerImageSize(typeof banner === "string" ? banner : ""))
    .filter(Boolean);
};

const normalizeExternalUrl = (url?: string | null) => {
  const trimmed = (url ?? "").trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const normalizeCompanyDomain = (input: string) => {
  let domain = input.trim().toLowerCase();
  if (!domain) return "";
  domain = domain.replace(/^https?:\/\//i, "");
  domain = domain.replace(/^www\./i, "");
  domain = domain.split("/")[0]?.split("?")[0]?.split("#")[0] ?? "";
  return domain;
};

const getCompanyDomain = (
  companyWebsite?: string | null,
  customerDomain?: string | null
) => {
  const fromCustomerDomain = normalizeCompanyDomain(customerDomain || "");
  if (fromCustomerDomain) return fromCustomerDomain;
  return normalizeCompanyDomain(companyWebsite || "");
};

/** Industry AI refresh is allowed when fetched_at is null/invalid or older than 3 months. */
const canRefetchIndustryFromTimestamp = (fetchedAt?: string | null) => {
  if (fetchedAt == null || String(fetchedAt).trim() === "") return true;
  const fetched = new Date(fetchedAt);
  if (Number.isNaN(fetched.getTime())) return true;
  const unlockAt = new Date(fetched);
  unlockAt.setMonth(unlockAt.getMonth() + 3);
  return Date.now() >= unlockAt.getTime();
};

/** Normalize API flags that may be boolean | 0/1 | "0"/"1" | "true"/"false". */
const isApiFetchedFlag = (value: unknown): boolean => {
  if (value === true || value === 1) return true;
  if (value === false || value === 0 || value == null) return false;
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    if (v === "true" || v === "1") return true;
    if (v === "false" || v === "0" || v === "") return false;
  }
  return Boolean(value);
};

const hasApolloOrganizationData = (apollo: unknown): boolean => {
  if (!apollo || typeof apollo !== "object") return false;
  const body = apollo as Record<string, unknown>;
  const org =
    body.organization && typeof body.organization === "object"
      ? (body.organization as Record<string, unknown>)
      : Array.isArray(body.organizations) &&
          body.organizations[0] &&
          typeof body.organizations[0] === "object"
        ? (body.organizations[0] as Record<string, unknown>)
        : body;
  return Boolean(
    org.id || org.name || org.primary_domain || org.website_url
  );
};

const mapCompanyBrandDetails = (company: any): CustomerDetails => {
  const socials = company?.socials ?? {};
  const socialBanners = normalizeSocialBanners(company?.socialBanners);
  return {
    ...company,
    brandName: company?.brandName,
    domain: company?.domain,
    customerImage:
      company?.customerImage || company?.imgUrl || company?.imageUrl || undefined,
    customerBannerImage: socialBanners[0] || company?.customerBannerImage,
    socialBanners,
    linkedinUrl: socials.linkedin || company?.linkedinUrl || undefined,
    facebookUrl: socials.facebook || company?.facebookUrl || undefined,
    instagramUrl: socials.instagram || company?.instagramUrl || undefined,
    youtubeUrl: socials.youtube || company?.youtubeUrl || undefined,
    twitterUrl:
      socials.twitter || company?.twitterUrl || company?.xUrl || undefined,
    industry: company?.industry,
    entityName: company?.entityName,
    entity_type: company?.entity_type,
    creditRating: company?.creditRating,
    networthYear: company?.networthYear,
    networthValue: company?.networthValue,
    turnoverYear: company?.turnoverYear,
    turnoverValue: company?.turnoverValue,
    creditRatingAgency: company?.creditRatingAgency,
    apolloOrganization:
      company?.apolloOrganization ??
      company?.apollo_organization ??
      undefined,
  };
};

const mergeBrandDetails = (
  fresh: CustomerDetails,
  prev?: CustomerDetails | null
): CustomerDetails => ({
  ...fresh,
  brandName: fresh.brandName || prev?.brandName,
  domain: fresh.domain || prev?.domain,
  customerImage: fresh.customerImage || prev?.customerImage,
  customerBannerImage: fresh.customerBannerImage || prev?.customerBannerImage,
  socialBanners: fresh.socialBanners?.length ? fresh.socialBanners : prev?.socialBanners,
  linkedinUrl: fresh.linkedinUrl || prev?.linkedinUrl,
  facebookUrl: fresh.facebookUrl || prev?.facebookUrl,
  instagramUrl: fresh.instagramUrl || prev?.instagramUrl,
  youtubeUrl: fresh.youtubeUrl || prev?.youtubeUrl,
  twitterUrl: fresh.twitterUrl || prev?.twitterUrl,
  industry: fresh.industry || prev?.industry,
  apolloOrganization: fresh.apolloOrganization ?? prev?.apolloOrganization,
});

const SocialIcon = ({
  type,
  className = "h-4 w-4",
}: {
  type: "linkedin" | "facebook" | "instagram" | "youtube" | "twitter";
  className?: string;
}) => {
  if (type === "linkedin") {
    return (
      <svg viewBox="0 0 24 24" fill="#ffffff" className={className} aria-hidden>
        <path d="M4.98 3.5C4.98 4.88 3.86 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1s2.48 1.12 2.48 2.5zM.5 8.5h4V23h-4V8.5zM8.5 8.5h3.8v2h.05c.53-1 1.82-2.05 3.75-2.05 4.01 0 4.75 2.64 4.75 6.07V23h-4v-6.6c0-1.57-.03-3.59-2.19-3.59-2.19 0-2.53 1.71-2.53 3.48V23h-4V8.5z" />
      </svg>
    );
  }
  if (type === "facebook") {
    return (
      <svg viewBox="0 0 24 24" fill="#ffffff" className={className} aria-hidden>
        <path d="M22 12.07C22 6.48 17.52 2 11.93 2S1.86 6.48 1.86 12.07c0 5.02 3.66 9.18 8.44 9.93v-7.02H7.9v-2.91h2.4V9.84c0-2.37 1.41-3.68 3.57-3.68 1.03 0 2.11.18 2.11.18v2.32h-1.19c-1.17 0-1.54.73-1.54 1.48v1.78h2.62l-.42 2.91h-2.2V22c4.78-.75 8.44-4.91 8.44-9.93z" />
      </svg>
    );
  }
  if (type === "instagram") {
    return (
      <svg viewBox="0 0 24 24" fill="#ffffff" className={className} aria-hidden>
        <path d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5zm0 2a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3H7zm11 1.5a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5zM12 7.5A4.5 4.5 0 1 1 12 16.5 4.5 4.5 0 0 1 12 7.5zm0 2A2.5 2.5 0 1 0 12 14.5 2.5 2.5 0 0 0 12 9.5z" />
      </svg>
    );
  }
  if (type === "twitter") {
    return (
      <svg viewBox="0 0 24 24" fill="#ffffff" className={className} aria-hidden>
        <path d="M18.244 2H21.5l-7.5 8.57L22.5 22h-6.57l-5.14-6.7L5.2 22H1.94l8.03-9.17L1.5 2h6.73l4.64 6.14L18.244 2zm-1.15 18h1.82L7.01 3.94H5.06L17.094 20z" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="#ffffff" className={className} aria-hidden>
      <path d="M23.5 6.2a3.05 3.05 0 0 0-2.15-2.16C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.35.54A3.05 3.05 0 0 0 .5 6.2 31.9 31.9 0 0 0 0 12a31.9 31.9 0 0 0 .5 5.8 3.05 3.05 0 0 0 2.15 2.16C4.5 20.5 12 20.5 12 20.5s7.5 0 9.35-.54a3.05 3.05 0 0 0 2.15-2.16A31.9 31.9 0 0 0 24 12a31.9 31.9 0 0 0-.5-5.8zM9.75 15.5v-7L15.5 12l-5.75 3.5z" />
    </svg>
  );
};

// Format currency function
const formatCurrency = (amount: number) => {
  if (amount >= 10000000) {
    return `₹${(amount / 10000000).toFixed(2)} Cr`;
  } else if (amount >= 100000) {
    return `₹${(amount / 100000).toFixed(2)} Lac`;
  } else {
    return `₹${amount?.toString()?.toLocaleString()}`;
  }
};

// Format date function
const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) return null;
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return null;
  }
};

// Calculate total charges
const calculateTotalCharges = (charges: any[]) => {
  return charges.reduce((total, charge) => total + (charge.amount || 0), 0);
};

// Transform GST details from camelCase to snake_case
const transformGstDetails = (gstDetails: any[]): any[] => {
  if (!Array.isArray(gstDetails)) return [];

  return gstDetails.map((detail) => {
    // Check if already in snake_case format (has company_name)
    const isAlreadySnakeCase = detail.company_name !== undefined;

    const transformed: any = {
      gstin: detail.gstin,
      status: detail.status,
      company_name: isAlreadySnakeCase
        ? detail.company_name
        : detail.companyName || "",
      trade_name: isAlreadySnakeCase
        ? detail.trade_name
        : detail.tradeName || "",
      state: detail.state,
      state_jurisdiction: isAlreadySnakeCase
        ? detail.state_jurisdiction
        : detail.stateJurisdiction || "",
      centre_jurisdiction: isAlreadySnakeCase
        ? detail.centre_jurisdiction
        : detail.centreJurisdiction || "",
      date_of_registration: isAlreadySnakeCase
        ? detail.date_of_registration
        : detail.dateOfRegistration || "",
      taxpayer_type: isAlreadySnakeCase
        ? detail.taxpayer_type
        : detail.taxpayerType || "",
      nature_of_business_activities: isAlreadySnakeCase
        ? detail.nature_of_business_activities
        : detail.natureOfBusinessActivities || "",
      filings: [],
    };

    // Transform filings array
    if (Array.isArray(detail.filings)) {
      transformed.filings = detail.filings.map((filing: any) => {
        const isFilingSnakeCase = filing.return_type !== undefined;
        return {
          return_type: isFilingSnakeCase
            ? filing.return_type
            : filing.returnType || "",
          date_of_filing: isFilingSnakeCase
            ? filing.date_of_filing
            : filing.dateOfFilling || filing.dateOfFiling || "",
          financial_year: isFilingSnakeCase
            ? filing.financial_year
            : filing.financialYear || "",
          tax_period: isFilingSnakeCase
            ? filing.tax_period
            : filing.taxPeriod || "",
          status: filing.status,
        };
      });
    }

    return transformed;
  });
};

const CustomerProfile = memo(() => {
  const { workflow } = useCustomerWorkflow();
  const { customerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  const tab = state?.tab;
  const DefaultsidebarTab = tab || "overview";



  const [poDetails, setPoDetails] = useState<any>(state?.poDetails || {});
  const [schedule, setSchedule] = useState<any>(state?.schedule || null);
  const [sidebarTab, setSidebarTab] = useState(DefaultsidebarTab);

  const { sidebarWidth } = useSidebar();
  const [loading, setLoading] = useState(true);
  const [isOnePagerOpen, setIsOnePagerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customerData, setCustomerData] = useState<CustomerData | null>(null);
  /** `entityType` from `sessionStorage.selectedCustomer` after profile load (kept in sync with session). */
  const [sessionEntityType, setSessionEntityType] = useState<number | null>(
    null
  );
  const [CustomerDetails, setCustomerDetails] =
    useState<CustomerDetails | null>(null);
  const [companySummary, setCompanySummary] = useState<{
    companySummary: {
      Company_Summary: string;
    };
    businessModel: {
      Business_Model: string;
    };
    customerInfo: {
      entityName: string;
      industry: string;
      customerName: string;
      customerPhoneNumber: string;
      customerEmail: string;
    };
    news: any;
    industryNews: any;
    /** Full industry summary API payload (`outlook` + `sources`). */
    sectorOutlook?: any;
    industryOutlook?: any;
  } | null>(null);
  const [editedAboutCompany, setEditedAboutCompany] = useState("");
  const [complianceTab, setComplianceTab] = useState("");
  const [editedBusinessModel, setEditedBusinessModel] = useState("");
  const [isCompanySummaryTouched, setIsCompanySummaryTouched] = useState(false);
  const [companySummaryIsFetched, setCompanySummaryIsFetched] = useState(false);
  const [companySummaryFetchedAt, setCompanySummaryFetchedAt] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [companies, setCompanies] = useState<companies | null>(null);
  const [ratioActionButton, setRatioActionButton] =
    useState<string>("AFIN Overview");
  const [onePageModelData, setOnePageModelData] =
    useState<onePageModelData | null>(null);
  const [activeSection, setActiveSection] = useState<
    "company" | "leasing" | "data-room" | "login" | "team-members" | null
  >("company");
  const [isDataUpdatePopupOpen, setIsDataUpdatePopupOpen] = useState(false);
  const [indiaState, setIndiaState] = useState<any[]>([]);
  const [addressManagementOpen, setAddressManagementOpen] = useState(false);
  const [rentalScheduleKey, setRentalScheduleKey] = useState<number>(0);
  const [loading2, setLoading2] = useState(false);
  const [isUploadingCustomerImage, setIsUploadingCustomerImage] = useState(false);
  const [websiteDomainDraft, setWebsiteDomainDraft] = useState("");
  const [isFetchingBrandData, setIsFetchingBrandData] = useState(false);
  const [isDomainEditorOpen, setIsDomainEditorOpen] = useState(false);
  const [adminUserList, setAdminUserList] = useState<AdminUserList[]>([]);
  // const [poDetailsPosId, setPoDetailsPosId] = useState("");


  const [addaddressManagementOpen, setAddAddressManagementOpen] =
    useState(false);
  const [addresses, setAddresses] = useState<any[]>([]);


  const [mraOpen, setMRAOpen] = useState(false);

  const [isMra, setIsMra] = useState(false);
  const [isTermSheet, setIsTermSheet] = useState(false);
  const [requirementData, setRequirementData] = useState<any>(null);
  // Handle navigation state with poDetails - only run once when component mounts with state
  const hasProcessedStateRef = useRef(false);
  /** Clears stale profile on customer switch during render so children never see the previous company. */
  const [profileCustomerId, setProfileCustomerId] = useState(customerId);

  if (customerId !== profileCustomerId) {
    setProfileCustomerId(customerId);
    setSidebarTab("overview");
    setWebsiteDomainDraft("");
    setIsDomainEditorOpen(false);
    setIsFetchingBrandData(false);
    setCustomerDetails(null);
    setCustomerData(null);
  }

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("selectedCustomer");
      if (!raw || !customerId) {
        setSessionEntityType(null);
        return;
      }
      const parsed = JSON.parse(raw) as {
        customerId?: string;
        entityType?: string | number | null;
      };
      if (String(parsed.customerId ?? "") === String(customerId)) {
        const et = parsed.entityType;
        setSessionEntityType(
          et != null && String(et).trim() !== "" ? Number(et) : null
        );
      } else {
        setSessionEntityType(null);
      }
    } catch {
      setSessionEntityType(null);
    }
  }, [customerId]);


  const handleRentalSchedule = (nextTab: string) => {
    setSidebarTab(nextTab);
    setRentalScheduleKey((prev) => prev + 1);
    if (customerId) {
      navigate(`/manage-customer/${customerId}`, { state: { tab: nextTab } });
    }
  };

  useEffect(() => {
    if (tab === "schedules-view") {
      handleRentalSchedule("schedules-view");
    }
  }, [schedule])


  // Handle navigation state with poDetails - only run once when component mounts with state
  useEffect(() => {
    fetchStates();
    fetchAdminUserList();
    if (state?.poDetails && !hasProcessedStateRef.current) {
      setPoDetails(state.poDetails);
      setSidebarTab("purchase-order-pos");
      hasProcessedStateRef.current = true;
    }

    if (state?.requirementDetails && !hasProcessedStateRef.current) {
      setRequirementData(state.requirementDetails);
      setSidebarTab("login-requirement-view");
      hasProcessedStateRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAdminUserList = async () => {
    try {
      const response = await apiService.get<{ data: AdminUserList[] }>(
        `/customers/get-admin-user-list-active`
      );
      const transformedDocs = response.data.map((doc) => ({
        name: doc.name,
        id: doc.id,
      }));
      setAdminUserList(transformedDocs);
    } catch (error) {
      console.error("Error fetching admin users:", error);
    }
  };

  const fetchStates = async () => {
    const response = await apiService.get<any>(`/supplier/get-india-state`);
    setIndiaState(response.data);
  };

  const getRatiosData = async () => {
    try {
      const response = await apiService.get<ApiResponse<any>>(
        `/customers/financials/get-Financial-Ratios/${customerId}`
      );
      setRatioActionButton(
        (response as ApiResponse<any>)?.actionButton || "AFIN Overview"
      );
    } catch (error) {
      console.error("Error fetching financial ratios:", error);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (sidebarTab === "overview") {
      getRatiosData();
    }
  }, [sidebarTab]);

  const creditRatings = [
    "AAA",
    "AA+",
    "AA",
    "AA-",
    "A+",
    "A",
    "A-",
    "BBB+",
    "BBB",
    "BBB-",
    "BB+",
    "BB",
    "BB-",
    "B+",
    "B",
    "B-",
    "CCC+",
    "CCC",
    "CCC-",
    "CC",
    "C",
    "D",
    "Unrated",
  ];

  useEffect(() => {
    const fullType = customerData?.company_details?.company?.classification;
    const shortType = fullType?.split(" ").slice(0, 2).join(" ");
    let customerSession = {
      legal_name: customerData?.company_details?.company?.legal_name,
      brand: CustomerDetails?.brandName,
      website: customerData?.company_details?.company?.website,
      entity_type: sessionEntityType ?? shortType,
      share_price: "-",
      market_cap: "-",
      group_company: "-",
      proposed_facility: "-",
      proposed_quantum: "-",
      proposed_collateral: "-",
      pan: customerData?.company_details?.company?.pan,
      cin:
        customerData?.company_details?.company?.cin ||
        customerData?.company_details?.company?.llpin,
      city: customerData?.company_details?.company?.registered_address?.city,
      state: customerData?.company_details?.company?.registered_address?.state,
      registered_address:
        customerData?.company_details?.company?.registered_address?.full_address,
      rating: "-",
      incorporation_date:
        customerData?.company_details?.company?.incorporation_date,
      creditRating: CustomerDetails?.creditRating || "",
      networthYear: CustomerDetails?.networthYear,
      networthValue: CustomerDetails?.networthValue,
      turnoverYear: CustomerDetails?.turnoverYear,
      turnoverValue: CustomerDetails?.turnoverValue,
      creditRatingAgency: CustomerDetails?.creditRatingAgency || "",
      last_updated_date: customerData?.company_details.metadata?.last_updated,
    };
    let businessSession = {
      industry: CustomerDetails?.industry,
      about_company: editedAboutCompany,
      business_model: editedBusinessModel,
    };

    let openCharges = customerData?.company_details?.open_charges;

    const directorSession = customerData?.director?.authorized_signatories
      ?.filter((signatory) => signatory.date_of_cessation == null)
      .map((signatory) => {
        const { name, din, designation, date_of_appointment } = signatory;

        let tenure = "";
        if (date_of_appointment) {
          const startDate = new Date(date_of_appointment);
          const now = new Date();

          const diffInMs = now.getTime() - startDate.getTime();
          const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

          if (diffInDays >= 365) {
            const years = Math.floor(diffInDays / 365);
            tenure = years === 1 ? "1 year" : `${years} years`;
          } else if (diffInDays >= 30) {
            const months = Math.floor(diffInDays / 30);
            tenure = months === 1 ? "1 month" : `${months} months`;
          } else {
            tenure = `${diffInDays} days`;
          }
        }

        return {
          name,
          din,
          designation,
          tenure,
        };
      });

    const gstSession = customerData?.gstEpfo?.gst_details
      ?.filter((detail) => detail.status === "Active")
      .map(({ gstin, state, date_of_registration }) => ({
        gstin,
        state,
        registration: date_of_registration,
      }));

    let shareHoldingMoreThenFivePercentSession =
      customerData?.director?.shareholdings_more_than_five_percent;

    let financialSession = customerData?.financial?.financials;

    let relatedPartyTransactions = {
      holding_entities: customerData?.company_details.holding_entities,
      joint_ventures: customerData?.company_details.joint_ventures,
      subsidiary_entities: customerData?.company_details.subsidiary_entities,
      associate_entities: customerData?.company_details.associate_entities,
      related_party_transactions:
        customerData?.company_details.related_party_transactions,
    };

    setOnePageModelData({
      company_details: customerSession,
      businessSession: businessSession,
      openChargesSession: openCharges,
      directorSession: directorSession,
      gstSession: gstSession,
      shareHoldingMoreThenFivePercentSession:
        shareHoldingMoreThenFivePercentSession,
      financialSession: financialSession,
      securities_allotment: customerData?.company_details.securities_allotment,
      relatedPartySection: relatedPartyTransactions,
      contribution_details:
        customerData?.company_details?.contribution_details || null,
    });
  }, [customerData, CustomerDetails, editedAboutCompany, editedBusinessModel, sessionEntityType]);

  const handlebrandNameChild = (data: string) => {
    setCustomerDetails((prev) => ({
      ...prev,
      brandName: data,
    }));
  };

  const handleCreditRatingChild = (data: string) => {
    setCustomerDetails((prev) => ({
      ...prev,
      creditRating: data,
    }));
  };

  const handleCreditRatingAgencyChild = (data: string) => {
    setCustomerDetails((prev) => ({
      ...prev,
      creditRatingAgency: data,
    }));
  };

  const handleTurnoverYearChild = (data: string) => {
    setCustomerDetails((prev) => ({
      ...prev,
      turnoverYear: data,
    }));
  };

  const handleNetworthYearChild = (data: string) => {
    setCustomerDetails((prev) => ({
      ...prev,
      networthYear: data,
    }));
  };

  const handleTurnoverValueChild = (data: string) => {
    setCustomerDetails((prev) => ({
      ...prev,
      turnoverValue: data,
    }));
  };

  const handleNetworthValueChild = (data: string) => {
    setCustomerDetails((prev) => ({
      ...prev,
      networthValue: data,
    }));
  };

  const fetchCustomerData = async () => {
    if (!customerId) {
      setError("Customer ID is required");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      let [customerResponse, summaryResponse] = await Promise.all([
        apiService.get<ApiResponse<CustomerData>>(
          `/customers/getCustomerBasicProfile/${customerId}`
        ),
        apiService.get<ApiResponse<any>>(
          `/customers/getCompanySummary/${customerId}`
        ),
      ]);
      if (customerResponse?.data) {
        const profileEntityType =
          customerResponse.data.company_details?.company?.entity_type ??
          (customerResponse as ApiResponse<CustomerData>).company
            ?.entity_type ??
          null;
        const sessionPayload = {
          customerId: customerId,
          companyName:
            customerResponse?.data?.company_details?.company?.legal_name ||
            customerResponse?.data?.company_details?.data?.data?.pnp?.legal_name ||
            customerId,
          city: customerResponse?.data?.company_details?.company
            ?.registered_address?.city,
          entityType: profileEntityType,
        };
        sessionStorage.setItem(
          "selectedCustomer",
          JSON.stringify(sessionPayload)
        );
        try {
          const raw = sessionStorage.getItem("selectedCustomer");
          if (raw) {
            const parsed = JSON.parse(raw) as {
              entityType?: string | number | null;
            };
            const et = parsed.entityType;
            setSessionEntityType(
              et != null && !isNaN(Number(et))
                ? Number(et) : null
            );
          } else {
            setSessionEntityType(null);
          }
        } catch {
          setSessionEntityType(null);
        }
        let company = {
          id: customerId,
          name:
            customerResponse?.data?.company_details?.company?.legal_name || customerResponse?.data?.company_details?.data?.data?.pnp?.legal_name ||
            customerId,
          city: customerResponse?.data?.company_details?.company?.registered_address
            ?.city,
          cin:
            customerResponse?.data?.company_details?.company?.cin ||
            customerResponse?.data?.company_details?.company?.llpin ||
            "",
        };
        useCustomerStore.getState().setCustomer(company);

        // Transform GST details if they exist
        if (customerResponse.data?.gstEpfo?.gst_details) {
          customerResponse.data.gstEpfo.gst_details = transformGstDetails(
            customerResponse.data.gstEpfo.gst_details
          );
        }

        const mappedCompany = mapCompanyBrandDetails(customerResponse.company);
        const profileDomain = getCompanyDomain(
          customerResponse.data?.company_details?.company?.website,
          mappedCompany.domain
        );

        setCustomerData({
          ...customerResponse.data,
          isComprehensiveApiFetched: isApiFetchedFlag(
            customerResponse.data?.isComprehensiveApiFetched
          ),
          company_details: customerResponse.data.company_details?.company
            ? {
                ...customerResponse.data.company_details,
                company: {
                  ...customerResponse.data.company_details.company,
                  website:
                    customerResponse.data.company_details.company.website?.trim() ||
                    (profileDomain ? `https://${profileDomain}` : ""),
                },
              }
            : customerResponse.data.company_details,
        });
        setAddresses(customerResponse.data.company_details?.company?.address);
        if (customerId) {
          fetchCustomerAddresses(customerId)
            .then(setAddresses)
            .catch(() => {});
        }
        setCustomerDetails(mappedCompany);
        setError(null);
      } else {
        setError("No data found for this customer");
      }

      if (summaryResponse?.data) {
        const summaryData = summaryResponse.data;
        const looksLikeOutlookPayload =
          summaryData.outlook != null ||
          summaryData.sector_risk != null ||
          (Array.isArray(summaryData.sources) &&
            summaryData.ai_generated != null);

        setCompanySummary({
          ...summaryData,
          sectorOutlook: looksLikeOutlookPayload
            ? summaryData
            : summaryData.sectorOutlook ??
              summaryData.sector_outlook ??
              summaryData.industryOutlook,
          industryNews: Array.isArray(summaryData.industryNews)
            ? summaryData.industryNews
            : Array.isArray(summaryData.industry_news)
              ? summaryData.industry_news
              : Array.isArray(summaryData.sources)
                ? summaryData.sources
                : summaryData.industryNews,
        });

        setCompanySummaryIsFetched(
          isApiFetchedFlag(summaryResponse.data.companySummaryIsFetched)
        );
        setCompanySummaryFetchedAt(
          summaryResponse.data.fetched_at ??
            summaryResponse.data.fetchedAt ??
            summaryResponse.data.generated_at ??
            null
        );

        setEditedAboutCompany(
          summaryResponse.data.companySummary.Company_Summary || ""
        );

        setEditedBusinessModel(
          summaryResponse.data.businessModel.Business_Model || ""
        );

      } else {
        setCompanySummaryIsFetched(false);
        setCompanySummaryFetchedAt(null);

        setEditedAboutCompany("");

        setEditedBusinessModel("");
      }
      getRatiosData();
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to fetch customer data");
      toast.error("Failed to load customer profile");
    } finally {
      setLoading(false);
    }
  };

  const onClickImageUpload = useCallback(
    async (
      event: React.ChangeEvent<HTMLInputElement>,
      documentType: string
    ) => {
      const file = event.target.files?.[0];
      if (!file || !customerId) return;

      if (!file.type.startsWith("image/")) {
        toast.error("Please upload an image file");
        event.target.value = "";
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image must be less than 5MB");
        event.target.value = "";
        return;
      }

      const documentCategory = "CUSTOMER_IMAGE";
      const previousImage = CustomerDetails?.customerImage;
      const previewUrl = URL.createObjectURL(file);

      setIsUploadingCustomerImage(true);
      setCustomerDetails((prev) => ({
        ...(prev ?? {}),
        customerImage: previewUrl,
      }));

      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("customerId", customerId);
        formData.append("documentType", documentType);
        formData.append("documentCategory", documentCategory);
        formData.append(
          "loggedInUser",
          String(getLocalStorageItem("detail")?.userId ?? "")
        );

        const response = await apiService.post<CustomerImageUploadResponse>(
          "/customers/upload-customer-image",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        const fileUrl = extractUploadedImageUrl(response);
        if (!fileUrl) {
          throw new Error("Upload succeeded but no image URL was returned");
        }

        URL.revokeObjectURL(previewUrl);
        setCustomerDetails((prev) => ({
          ...(prev ?? {}),
          customerImage: fileUrl,
        }));
        toast.success(response?.message || "Customer photo updated");
      } catch (error) {
        console.error("Error uploading file:", error);
        URL.revokeObjectURL(previewUrl);
        setCustomerDetails((prev) => ({
          ...(prev ?? {}),
          customerImage: previousImage,
        }));
        toast.error(
          (error as { message?: string })?.message ||
            "Failed to upload customer photo"
        );
      } finally {
        setIsUploadingCustomerImage(false);
        event.target.value = "";
      }
    },
    [customerId, CustomerDetails?.customerImage]
  );

  const socialLinks = (
    [
      {
        key: "linkedinUrl" as const,
        label: "LinkedIn",
        href: normalizeExternalUrl(CustomerDetails?.linkedinUrl),
        type: "linkedin" as const,
      },
      {
        key: "facebookUrl" as const,
        label: "Facebook",
        href: normalizeExternalUrl(CustomerDetails?.facebookUrl),
        type: "facebook" as const,
      },
      {
        key: "instagramUrl" as const,
        label: "Instagram",
        href: normalizeExternalUrl(CustomerDetails?.instagramUrl),
        type: "instagram" as const,
      },
      {
        key: "youtubeUrl" as const,
        label: "YouTube",
        href: normalizeExternalUrl(CustomerDetails?.youtubeUrl),
        type: "youtube" as const,
      },
      {
        key: "twitterUrl" as const,
        label: "Twitter",
        href: normalizeExternalUrl(CustomerDetails?.twitterUrl),
        type: "twitter" as const,
      },
    ] as const
  ).filter((link) => !!link.href);

  const bannerImages =
    CustomerDetails?.socialBanners?.length
      ? CustomerDetails.socialBanners
      : CustomerDetails?.customerBannerImage
        ? [CustomerDetails.customerBannerImage]
        : [];
  const hasBannerImage = bannerImages.length > 0;

  const brandDomain = normalizeCompanyDomain(CustomerDetails?.domain || "");
  const websiteFromProfile = normalizeCompanyDomain(
    customerData?.company_details?.company?.website || ""
  );
  const resolvedBrandDomain = brandDomain || websiteFromProfile;
  const hasApolloData = hasApolloOrganizationData(
    CustomerDetails?.apolloOrganization
  );
  // Show domain Run when domain or Apollo enrich is missing; hide when both exist.
  const showDomainRun = !resolvedBrandDomain || !hasApolloData;

  useEffect(() => {
    // Always sync from the current customer — never keep a previous company's draft.
    setWebsiteDomainDraft(resolvedBrandDomain || "");
  }, [customerId, resolvedBrandDomain]);

  const handleFetchCompanyByDomain = async () => {
    const domain =
      normalizeCompanyDomain(websiteDomainDraft) || resolvedBrandDomain;
    if (!domain) {
      toast.error("Enter a valid company domain (e.g. company.com)");
      return;
    }
    if (!customerId) {
      toast.error("Customer ID is missing");
      return;
    }

    setIsFetchingBrandData(true);
    try {
      const enrichResponse: any = await apiService.post(
        "/customers/enrich-company-by-domain",
        {
          customerId,
          domain,
        }
      );

      const enrichCompany =
        enrichResponse?.company ?? enrichResponse?.data ?? enrichResponse;
      const mappedFromEnrich = mapCompanyBrandDetails(enrichCompany);
      const resolvedDomain =
        mappedFromEnrich.domain || domain;
      const resolvedWebsite = `https://${resolvedDomain}`;

      setCustomerDetails(mappedFromEnrich);
      setCustomerData((prev) => {
        if (!prev?.company_details?.company) return prev;
        return {
          ...prev,
          company_details: {
            ...prev.company_details,
            company: {
              ...prev.company_details.company,
              website: resolvedWebsite,
            },
          },
        };
      });

      try {
        const customerResponse = await apiService.get<ApiResponse<CustomerData>>(
          `/customers/getCustomerBasicProfile/${customerId}`
        );

        if (customerResponse?.company) {
          const mappedProfile = mapCompanyBrandDetails(customerResponse.company);
          const profileDomain = getCompanyDomain(
            customerResponse.data?.company_details?.company?.website,
            mappedProfile.domain
          );
          setCustomerDetails((prev) =>
            mergeBrandDetails(mappedProfile, prev ?? mappedFromEnrich)
          );
          if (customerResponse.data) {
            setCustomerData({
              ...customerResponse.data,
              company_details: customerResponse.data.company_details?.company
                ? {
                    ...customerResponse.data.company_details,
                    company: {
                      ...customerResponse.data.company_details.company,
                      website:
                        customerResponse.data.company_details.company.website?.trim() ||
                        (profileDomain ? `https://${profileDomain}` : resolvedWebsite),
                    },
                  }
                : customerResponse.data.company_details,
            });
          }
        } else if (customerResponse?.data) {
          setCustomerData(customerResponse.data);
        }
      } catch (refreshError) {
        console.error("Error refreshing customer profile:", refreshError);
      }

      setWebsiteDomainDraft(resolvedDomain);
      setIsDomainEditorOpen(false);
      toast.success(enrichResponse?.message || "Company brand data fetched");
    } catch (error) {
      console.error("Error enriching company by domain:", error);
      toast.error(
        (error as { message?: string })?.message ||
          "Failed to fetch company image and data"
      );
    } finally {
      setIsFetchingBrandData(false);
    }
  };

  const getCustomerList = () => {
    apiService
      .get<ApiResponse<companies>>(
        `/customers/companyList?customerId=${customerId}`
      )
      .then((response) => {
        setCompanies(response.data);
      });
  };

  const handlefinancialupload = async (
    data: FinancialData,
    type: string,
    nature: string
  ) => {
    let response: any;
    try {
      setLoading(true);
      response = await apiService.post(
        `/customers/uploadFinancialData/${customerId}`,
        {
          financialdata: data,
          type: type,
          nature: nature,
        }
      );

      setCustomerData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          financial: { financials: response.data },
        };
      });
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomerData();
    getCustomerList();
  }, [customerId]);

  // Effect to handle scroll and make header sticky
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Save company summary function
  const saveCompanySummary = async (
    aboutCompany: string,
    businessModel: string
  ) => {
    if (!customerId) return;
    if (isSaving) return;
    if (!isCompanySummaryTouched) return;

    const normalize = (v: unknown) => (typeof v === "string" ? v.trim() : "");
    const prevAbout = normalize(companySummary?.companySummary?.Company_Summary);
    const prevBusiness = normalize(companySummary?.businessModel?.Business_Model);
    const nextAbout = normalize(aboutCompany);
    const nextBusiness = normalize(businessModel);
    const allowEmptyTouchedSave = nextAbout === "" || nextBusiness === "";

    // Allow touched saves for empty values; otherwise skip only if truly unchanged.
    if (!allowEmptyTouchedSave && nextAbout === prevAbout && nextBusiness === prevBusiness) {
      return;
    }

    try {
      setIsSaving(true);
      await apiService.post(`/customers/saveCompanySummary/${customerId}`, {
        companySummary: {
          Company_Summary: aboutCompany,
        },
        businessModel: {
          Business_Model: businessModel,
        },
      });

      // Keep local "saved" values in sync so debounce/blur doesn't re-post.
      setCompanySummary((prev) =>
        prev
          ? {
            ...prev,
            companySummary: { Company_Summary: aboutCompany },
            businessModel: { Business_Model: businessModel },
          }
          : prev
      );
      setIsCompanySummaryTouched(false);
    } catch (error) {
      console.error("Error saving company summary:", error);
      toast.error("Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  };

  // Handle text changes with debounce
  const isFirstRun = useRef(true);

  // useEffect(() => {
  //   if (isFirstRun.current) {
  //     isFirstRun.current = false;
  //     return; // Skip on first render
  //   }
  //   if (!isCompanySummaryTouched) return;
  //   const timeoutId = setTimeout(() => {
  //     const isEmptyTouchedSave =
  //       editedAboutCompany.trim() === "" || editedBusinessModel.trim() === "";
  //     if (
  //       isEmptyTouchedSave ||
  //       editedAboutCompany !==
  //         companySummary?.companySummary?.Company_Summary ||
  //       editedBusinessModel !== companySummary?.businessModel?.Business_Model
  //     ) {
  //       saveCompanySummary(editedAboutCompany, editedBusinessModel);
  //     }
  //   }, 1000);

  //   return () => clearTimeout(timeoutId);
  // }, [editedAboutCompany, editedBusinessModel, companySummary, isCompanySummaryTouched]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen]);

  // Handle blur event
  const handleBlur = () => {
    if (!isCompanySummaryTouched) return;
    const isEmptyTouchedSave =
      editedAboutCompany.trim() === "" || editedBusinessModel.trim() === "";
    if (
      isEmptyTouchedSave ||
      editedAboutCompany !== companySummary?.companySummary?.Company_Summary ||
      editedBusinessModel !== companySummary?.businessModel?.Business_Model
    ) {
      saveCompanySummary(editedAboutCompany, editedBusinessModel);
    }
  };

  const saveCreditRating = async (value: string) => {
    try {
      // Optionally show a loading state
      await apiService.post("/customers/updateCustomerField", {
        customer_id: customerId,
        credit_rating: value,
      });
      handleCreditRatingChild(value);
      toast.success("Credit rating updated successfully");
    } catch (error) {
      toast.error("Failed to update credit rating");
    }
  };

  const overviewFinancialChartData = useMemo(() => {
    const financial = customerData?.financial;
    const financials =
      financial &&
      !Array.isArray(financial) &&
      Array.isArray(financial.financials)
        ? financial.financials
        : [];
    if (!financials.length) return [] as FinancialDataMatric[];

    try {
      const top3 = getDefaultRecords(financials as any[]);
      if (!top3.length) return [] as FinancialDataMatric[];
      const isLLPIN = top3.some(
        (f: any) =>
          f?.isLLPIN ||
          f?.statement_of_assets_and_liabilities ||
          f?.statement_of_income_and_expenditure
      );
      const calculated = isLLPIN
        ? calculateLLPFinancialRatios(top3)
        : calculateFinancialRatios(top3);
      return Array.isArray(calculated)
        ? (calculated as FinancialDataMatric[])
        : [];
    } catch (error) {
      console.error("Failed to build overview financial chart data", error);
      return [] as FinancialDataMatric[];
    }
  }, [customerData?.financial]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error || !customerData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Error Loading Profile
          </h2>
          <p className="text-gray-600">{error || "No data available"}</p>
        </div>
      </div>
    );
  }

  const fetchgetCompanySummary = async () => {
    setLoading2(true);
    try {
      const response = await apiService.post<ApiResponse<any>>(
        `/customers/getCompanySummary`,
        {
          customerId: customerId,
        }
      );
      if (response.data) {
        const summaryData = response.data;
        const looksLikeOutlookPayload =
          summaryData.outlook != null ||
          summaryData.sector_risk != null ||
          (Array.isArray(summaryData.sources) &&
            summaryData.ai_generated != null);

        setCompanySummary({
          ...summaryData,
          sectorOutlook: looksLikeOutlookPayload
            ? summaryData
            : summaryData.sectorOutlook ??
              summaryData.sector_outlook ??
              summaryData.industryOutlook,
          industryNews: Array.isArray(summaryData.industryNews)
            ? summaryData.industryNews
            : Array.isArray(summaryData.industry_news)
              ? summaryData.industry_news
              : Array.isArray(summaryData.sources)
                ? summaryData.sources
                : summaryData.industryNews,
        });

        // Set the about company text if it exists
        setEditedAboutCompany(response.data.companySummary.Company_Summary);


        // Set the business model text if it exists
        // Remove the "Here is the summary:" prefix if it exists
        const businessModel = response.data.businessModel.Business_Model;
        const cleanedBusinessModel = businessModel.replace(
          /^Here is the summary:\n\n/,
          ""
        );
        setEditedBusinessModel(cleanedBusinessModel);

        setCompanySummaryIsFetched(
          response.data.companySummaryIsFetched
        );
        setCompanySummaryFetchedAt(
          response.data.fetched_at ??
            response.data.fetchedAt ??
            response.data.generated_at ??
            null
        );
      }
    } catch (error) {
      console.error("Error fetching company summary:", error);
      toast.error("Failed to generate AI summary");
    } finally {
      setLoading2(false);
    }
  };

  const fetchIndustrySummary = async () => {
    if (!customerId) {
      toast.error("Customer ID is missing");
      return;
    }
    if (
      companySummaryIsFetched &&
      !canRefetchIndustryFromTimestamp(companySummaryFetchedAt)
    ) {
      toast.error("Industry data can be refreshed only after 3 months");
      return;
    }

    setLoading2(true);
    try {
      const response = await apiService.post<ApiResponse<any>>(
        `/customers/getIndustrySummary`,
        { customerId }
      );
      const data = response?.data ?? response;
      if (data) {
        // Shape: { industryOutlook: {...}, customerInfo: {...}, fetchedAt }
        const outlookBlock =
          data.industryOutlook ??
          data.industry_outlook ??
          data.outlook ??
          data.sectorOutlook ??
          data.sector_outlook ??
          null;

        const sourceList = Array.isArray(outlookBlock?.sources)
          ? outlookBlock.sources
          : Array.isArray(data.industryNews)
            ? data.industryNews
            : Array.isArray(data.industry_news)
              ? data.industry_news
              : Array.isArray(data.sources)
                ? data.sources
                : null;

        setCompanySummary((prev) => ({
          ...(prev ?? {
            companySummary: { Company_Summary: editedAboutCompany },
            businessModel: { Business_Model: editedBusinessModel },
            customerInfo: {
              entityName: "",
              industry: "",
              customerName: "",
              customerPhoneNumber: "",
              customerEmail: "",
            },
            news: null,
            industryNews: null,
          }),
          customerInfo: {
            ...(prev?.customerInfo ?? {
              entityName: "",
              industry: "",
              customerName: "",
              customerPhoneNumber: "",
              customerEmail: "",
            }),
            ...(data.customerInfo ?? {}),
            industry:
              data.customerInfo?.industry ||
              prev?.customerInfo?.industry ||
              "",
          },
          industryNews: sourceList ?? prev?.industryNews ?? null,
          news: data.news ?? prev?.news,
          // Store the industryOutlook object (Title Case metrics + Growth Drivers / Key Risks).
          sectorOutlook: outlookBlock ?? prev?.sectorOutlook,
          industryOutlook: outlookBlock ?? prev?.industryOutlook,
        }));
        setCompanySummaryFetchedAt(
          data.fetchedAt ??
            data.fetched_at ??
            outlookBlock?.generated_at ??
            data.generated_at ??
            null
        );
        toast.success(
          (response as any)?.message ||
            data.message ||
            "Industry data refreshed"
        );
      }
    } catch (error) {
      console.error("Error fetching industry summary:", error);
      toast.error("Failed to generate industry outlook");
    } finally {
      setLoading2(false);
    }
  };

  const handleIndustryGenerateAI = async () => {
    if (!companySummaryIsFetched) {
      await fetchgetCompanySummary();
      return;
    }
    await fetchIndustrySummary();
  };

  const canGenerateIndustry =
    !companySummaryIsFetched ||
    companySummaryFetchedAt == null ||
    String(companySummaryFetchedAt).trim() === "" ||
    canRefetchIndustryFromTimestamp(companySummaryFetchedAt);

  const toggleDropdown = () => {
    setIsMenuOpen((prev) => !prev);
  };

  const handleFetchCompanyComprehensiveSummary = async () => {
    setLoading(true);
    try {
      let response = await apiService.post<ApiResponse<any>>(
        `/customers/getCompanyComprehensiveSummary`,
        {
          customerId: customerId,
        }
      );

      // Transform GST details if they exist
      if (response.data?.gstEpfo?.gst_details) {
        response.data.gstEpfo.gst_details = transformGstDetails(
          response.data.gstEpfo.gst_details
        );
      }

      setCustomerData({
        ...response.data,
        isComprehensiveApiFetched: isApiFetchedFlag(
          response.data?.isComprehensiveApiFetched ?? true
        ),
      });
      setIsDataUpdatePopupOpen(false);
    } catch (err) {
      setError("something when wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleUnlockClick = async () => {
    setIsDataUpdatePopupOpen(true);
  };

  const handlefinancialrefresh = () => {
    getRatiosData();
  };

  const handleAddressDialog = (open: boolean) => {
    setAddressManagementOpen(open);
    setAddAddressManagementOpen(open);
  };

  const handleUpdateAddresses = (addresses: any[]) => {
    setAddresses([...addresses]);
    // const updatedSupplier = { ...supplier, addresses };
    // onEdit(updatedSupplier);
  };

  const handleViewPos = (po: any) => {
    setPoDetails(po);
    setSidebarTab("purchase-order-pos");
  };

  const getAddressLabel = (type?: string) => getAddressTypeLabel(type);

  type CustomerDocumentType = "master_rental_agreement";


  type CustomerDocument = {
    documentId: string;
    customerId: string;
    documentType: CustomerDocumentType;
    documentName: string;
    uploadedAt: string;
    fileUrl: string;
    status: string;
    createdBy: string;
    isActive: boolean;
    uploadedDate: string;    // NEW
    uploadedBy: string;      // NEW
    fileName: string;        // NEW
    fileSize: number;
  };

  const checkAlert = () => {
    setMRAOpen(true)
  };

  const getDocUploads = (getDocData: any) => {
    setIsMra(getDocData.mra);
    setIsTermSheet(getDocData.termsheet);
  }

  const handleViewRequirement = (requirementData: any) => {
    setSidebarTab("login-requirement-view");
    setRequirementData(requirementData);
  }


  const businesses = customerData?.company_details?.data?.data?.pnp?.businesses || [];

  return (
    <TooltipProvider>
      <div className="flex overflow-hidden w-[100%] bg-white">
        {error && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 m-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-yellow-400 mr-2" />
              <div>
                <h3 className="text-sm font-medium text-yellow-800">Notice</h3>
                <p className="text-sm text-yellow-700">{error}</p>
              </div>
            </div>
          </div>
        )}
        {/* Sidebar */}
        <aside
          className="w-[16%] bg-white rounded-xl border border-[#e3d6b7] fixed top-16 h-[calc(100vh-56px)] z-40 overflow-y-auto transition-all duration-300"
          style={{ left: `${sidebarWidth}px` }}
        >
          {/* Sidebar content */}
          <div className="p-2 space-y-2">
            {/* Navigation Menu */}
            <nav className="space-y-2">
              {/* Company Profile Header with Accordion */}
              <Collapsible
                open={activeSection === "company"}
                onOpenChange={(isOpen) => {
                  setActiveSection(isOpen ? "company" : null);
                }}
              >
                <CollapsibleTrigger className="w-full text-left px-4 py-3 rounded-lg flex items-center justify-between">
                  <div className="flex items-center hover:underline hover:underline-offset-1 transition">
                    <Building className="h-4 w-4 mr-3" />
                    Company Profile
                  </div>

                  <span aria-hidden>
                    {activeSection === "company" ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </span>
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-1 ml-4 space-y-1">
                  <button
                    className={`w-full text-left px-4 py-2 rounded-lg flex items-center ${sidebarTab === "overview"
                      ? "bg-blue-50 text-blue-700 font-medium"
                      : "hover:bg-gray-50 text-gray-600 hover:text-gray-800"
                      }`}
                    onClick={() => setSidebarTab("overview")}
                  >
                    <Info className="h-4 w-4 mr-3" />
                    Overview
                  </button>
                  {sessionEntityType !== 4 && sessionEntityType !== 5 && (
                    <>
                      <button
                        className={`w-full text-left px-4 py-2 rounded-lg flex items-center ${sidebarTab === "director"
                          ? "bg-blue-50 text-blue-700 font-medium"
                          : "hover:bg-gray-50 text-gray-600 hover:text-gray-800"
                          }`}
                        onClick={() => setSidebarTab("director")}
                      >
                        <Users className="h-4 w-4 mr-3" />
                        Management Profile
                      </button>

                      {customerData?.gstEpfo?.gst_details ||
                        customerData?.gstEpfo?.establishments_registered_with_epfo ? (
                        <button
                          className={`w-full text-left px-4 py-2 rounded-lg flex items-center ${sidebarTab === "compliance"
                            ? "bg-blue-50 text-blue-700 font-medium"
                            : "hover:bg-gray-50 text-gray-600 hover:text-gray-800"
                            }`}
                          onClick={() => {
                            setSidebarTab("compliance");
                            setComplianceTab("gst");
                          }}
                        >
                          <AlertCircle className="h-4 w-4 mr-3" />
                          Compliance
                        </button>
                      ) : (
                        <button
                          disabled
                          className="w-full text-left px-4 py-2 rounded-lg opacity-50 cursor-not-allowed flex items-center justify-between"
                        >
                          <div className="flex items-center">
                            <AlertCircle className="h-4 w-4 mr-3" />
                            Compliance
                          </div>
                          <Lock className="h-4 w-4 text-gray-400" />
                        </button>
                      )}

                      {customerData.company_details.legal_history ? (
                        <button
                          className={`w-full text-left px-4 py-2 rounded-lg flex items-center ${sidebarTab === "legal"
                            ? "bg-blue-50 text-blue-700 font-medium"
                            : "hover:bg-gray-50 text-gray-600 hover:text-gray-800"
                            }`}
                          onClick={() => setSidebarTab("legal")}
                        >
                          <FileText className="h-4 w-4 mr-3" />
                          Legal
                        </button>
                      ) : (
                        <button
                          disabled
                          className="w-full text-left px-4 py-2 rounded-lg opacity-50 cursor-not-allowed flex items-center justify-between"
                        >
                          <div className="flex items-center">
                            <FileText className="h-4 w-4 mr-3" />
                            Legal
                          </div>
                          <Lock className="h-4 w-4 text-gray-400" />
                        </button>
                      )}

                      {customerData.financial ? (
                        <button
                          className={`w-full text-left px-4 py-2 rounded-lg flex items-center ${sidebarTab === "financial"
                            ? "bg-blue-50 text-blue-700 font-medium"
                            : "hover:bg-gray-50 text-gray-600 hover:text-gray-800"
                            }`}
                          onClick={() => setSidebarTab("financial")}
                        >
                          <BarChart className="h-4 w-4 mr-3" />
                          Financial
                        </button>
                      ) : (
                        <button
                          disabled
                          className="w-full text-left px-4 py-2 rounded-lg opacity-50 cursor-not-allowed flex items-center justify-between"
                        >
                          <div className="flex items-center">
                            <BarChart className="h-4 w-4 mr-3" />
                            Financial
                          </div>
                          <Lock className="h-4 w-4 text-gray-400" />
                        </button>
                      )}
                    </>
                  )}
                  {(sessionEntityType !== 4 && sessionEntityType !== 5) && customerData?.peerComparison?.peer_comparison &&
                    customerData?.peerComparison?.peer_comparison.length >
                    0 && (
                      <button
                        className={`w-full text-left px-4 py-2 rounded-lg flex items-center ${sidebarTab === "peer-comparison"
                          ? "bg-blue-50 text-blue-700 font-medium"
                          : "hover:bg-gray-50 text-gray-600 hover:text-gray-800"
                          }`}
                        onClick={() => setSidebarTab("peer-comparison")}
                      >
                        <Compass className="h-4 w-4 mr-3" />
                        Peer Comparison
                      </button>
                    )}
                  {sessionEntityType !== 4 && sessionEntityType !== 5 && (
                    <>
                      {customerData.company_details.holding_entities ||
                        customerData.company_details.subsidiary_entities ||
                        customerData.company_details.associate_entities ||
                        customerData.company_details.joint_ventures ||
                        customerData.company_details.related_party_transactions ? (
                        <button
                          className={`w-full text-left px-4 py-2 rounded-lg flex items-center ${sidebarTab === "related-parties"
                            ? "bg-blue-50 text-blue-700 font-medium"
                            : "hover:bg-gray-50 text-gray-600 hover:text-gray-800"
                            }`}
                          onClick={() => setSidebarTab("related-parties")}
                        >
                          <Users className="h-4 w-4 mr-3" />
                          Related Parties
                        </button>
                      ) : (
                        <button
                          disabled
                          className="w-full text-left px-4 py-2 rounded-lg opacity-50 cursor-not-allowed flex items-center justify-between"
                        >
                          <div className="flex items-center">
                            <Users className="h-4 w-4 mr-3" />
                            Related Parties
                          </div>
                          <Lock className="h-4 w-4 text-gray-400" />
                        </button>
                      )}
                    </>
                  )}
                  {(sessionEntityType !== 4 && sessionEntityType !== 5) && customerData.company_details.securities_allotment && (
                    <button
                      className={`w-full text-left px-4 py-2 rounded-lg flex items-center ${sidebarTab === "securities-allotment"
                        ? "bg-blue-50 text-blue-700 font-medium"
                        : "hover:bg-gray-50 text-gray-600 hover:text-gray-800"
                        }`}
                      onClick={() => setSidebarTab("securities-allotment")}
                    >
                      <FileText className="h-4 w-4 mr-3" />
                      Securities Allotment
                    </button>
                  )}

                  {(sessionEntityType !== 4 && sessionEntityType !== 5) && (customerData?.director?.shareholdings ||
                    customerData?.director?.director_shareholdings ||
                    customerData?.director
                      ?.shareholdings_more_than_five_percent) && (
                      <button
                        className={`w-full text-left px-4 py-2 rounded-lg flex items-center ${sidebarTab === "shareholding"
                          ? "bg-blue-50 text-blue-700 font-medium"
                          : "hover:bg-gray-50 text-gray-600 hover:text-gray-800"
                          }`}
                        onClick={() => setSidebarTab("shareholding")}
                      >
                        <PieChartOutlined className="h-4 w-4 mr-3" />
                        Shareholding
                      </button>
                    )}

                  {(sessionEntityType !== 4 && sessionEntityType !== 5) && (customerData?.company_details as any)
                    ?.contribution_details && (
                      <button
                        className={`w-full text-left px-4 py-2 rounded-lg flex items-center ${sidebarTab === "llp-contribution"
                          ? "bg-blue-50 text-blue-700 font-medium"
                          : "hover:bg-gray-50 text-gray-600 hover:text-gray-800"
                          }`}
                        onClick={() => setSidebarTab("llp-contribution")}
                      >
                        <PieChartOutlined className="h-4 w-4 mr-3" />
                        LLP Contribution
                      </button>
                    )}

                  <button
                    className={`w-full text-left px-4 py-2 rounded-lg flex items-center ${sidebarTab === "debt-profile"
                      ? "bg-blue-50 text-blue-700 font-medium"
                      : "hover:bg-gray-50 text-gray-600 hover:text-gray-800"
                      }`}
                    onClick={() => setSidebarTab("debt-profile")}
                  >
                    <CreditCardOutlined className="h-4 w-4 mr-3" />
                    Debt Profile
                  </button>

                  <button
                    className={`w-full text-left px-4 py-2 rounded-lg flex items-center ${sidebarTab === "credit-notes"
                      ? "bg-blue-50 text-blue-700 font-medium"
                      : "hover:bg-gray-50 text-gray-600 hover:text-gray-800"
                      }`}
                    onClick={() => setSidebarTab("credit-notes")}
                  >
                    <NotebookPen className="h-4 w-4 mr-3" />
                    Notes
                  </button>
                </CollapsibleContent>
              </Collapsible>

              <Collapsible
                open={activeSection === "login"}
                onOpenChange={(isOpen) => {
                  setActiveSection(isOpen ? "login" : null);
                }}
              >
                <CollapsibleTrigger className="w-full text-left px-4 py-3 rounded-lg flex items-center justify-between">
                  <div
                    onClick={() => setSidebarTab("login")}
                    className="flex items-center hover:underline hover:underline-offset-1 transition"
                  >
                    <ReceiptIndianRupee className="h-4 w-4 mr-3" />
                    Login
                  </div>
                </CollapsibleTrigger>
              </Collapsible>
              {/* Leasing Header with Accordion */}
              <Collapsible
                open={activeSection === "leasing"}
                onOpenChange={(isOpen) => {
                  setActiveSection(isOpen ? "leasing" : null);
                }}
              >
                <CollapsibleTrigger className="w-full text-left px-4 py-3 rounded-lg flex items-center justify-between">
                  <div className="flex items-center hover:underline hover:underline-offset-1 transition">
                    <ShoppingCart className="h-4 w-4 mr-3" />
                    Leasing
                  </div>
                  <span aria-hidden>
                    {activeSection === "leasing" ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </span>
                </CollapsibleTrigger>

                <CollapsibleContent className="mt-1 ml-4 space-y-1">
                  <Button
                    className={`w-full text-left px-4 py-2 rounded-lg flex items-center justify-between disabled:cursor-not-allowed ${sidebarTab === "rental-calculator"
                      ? "bg-blue-50 text-blue-700 font-medium"
                      : "hover:bg-gray-50 text-gray-600 hover:text-gray-800 "
                      }`}
                    onClick={() => setSidebarTab("rental-calculator")}
                    disabled={!workflow.calculation.enabled}
                    tooltip={workflow.calculation.reason}
                    variant="textOnly"
                  >
                    <div className="flex items-center">
                      <FileText className="h-4 w-4 mr-3" />
                      Calculator
                    </div>
                    {!workflow.calculation.enabled && <Lock className="h-4 w-4 text-gray-400" />}
                  </Button>
                  <Button
                    className={`w-full text-left px-4 py-2 rounded-lg flex items-center justify-between disabled:cursor-not-allowed ${sidebarTab === "purchase-order"
                      ? "bg-blue-50 text-blue-700 font-medium"
                      : "hover:bg-gray-50 text-gray-600 hover:text-gray-800"
                      }`}
                    onClick={() => setSidebarTab("purchase-order")}
                    disabled={!workflow.purchaseOrder.enabled}
                    tooltip={workflow.purchaseOrder.reason}
                    variant="textOnly"
                  >
                    <div className="flex items-center">
                      <ShoppingCart className="h-4 w-4 mr-3" />
                      Purchase Order
                    </div>
                    {!workflow.purchaseOrder.enabled && <Lock className="h-4 w-4 text-gray-400" />}
                  </Button>

                  <Button
                    className={`w-full text-left px-4 py-2 rounded-lg flex items-center justify-between disabled:cursor-not-allowed ${sidebarTab === "rental-schedule"
                      ? "bg-blue-50 text-blue-700 font-medium"
                      : "hover:bg-gray-50 text-gray-600 hover:text-gray-800"
                      }`}
                    onClick={() => handleRentalSchedule("rental-schedule")}
                    disabled={!workflow.rentalSchedule.enabled}
                    tooltip={workflow.rentalSchedule.reason}
                    variant="textOnly"
                  >
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-3" />
                      Rental Schedule
                    </div>
                    {!workflow.rentalSchedule.enabled && <Lock className="h-4 w-4 text-gray-400" />}
                  </Button>

                  <Button
                    className={`w-full text-left px-4 py-2 rounded-lg flex items-center justify-between disabled:cursor-not-allowed ${sidebarTab === "notice-of-assignment"
                      ? "bg-blue-50 text-blue-700 font-medium"
                      : "hover:bg-gray-50 text-gray-600 hover:text-gray-800"
                      }`}
                    onClick={() => handleRentalSchedule("notice-of-assignment")}
                    disabled={!workflow.noa.enabled}
                    variant="textOnly"
                    tooltip={workflow.noa.reason}
                  >
                    <div className="flex items-center">
                      <FileText className="h-4 w-4 mr-3" />
                      Notice of Assignment
                    </div>
                    {!workflow.noa.enabled && <Lock className="h-4 w-4 text-gray-400" />}
                  </Button>
                  <Button
                    className={`w-full text-left px-4 py-2 rounded-lg flex items-center justify-between disabled:cursor-not-allowed ${sidebarTab === "invoices"
                      ? "bg-blue-50 text-blue-700 font-medium"
                      : "hover:bg-gray-50 text-gray-600 hover:text-gray-800"
                      }`}
                    onClick={() => handleRentalSchedule("invoices")}
                    disabled={!workflow.invoice.enabled}
                    tooltip={workflow.invoice.reason}
                    variant="textOnly"
                  >
                    <div className="flex items-center">
                      <ReceiptIndianRupee className="h-4 w-4 mr-3" />
                      Invoice
                    </div>
                    {!workflow.invoice.enabled && <Lock className="h-4 w-4 text-gray-400" />}
                  </Button>
                </CollapsibleContent>
              </Collapsible>

              <Collapsible
                open={activeSection === "data-room"}
                onOpenChange={(isOpen) => {
                  setActiveSection(isOpen ? "data-room" : null);
                }}
              >
                <CollapsibleTrigger className="w-full text-left px-4 py-3 rounded-lg flex items-center justify-between">
                  <div
                    onClick={() => setSidebarTab("data-room")}
                    className="flex items-center hover:underline hover:underline-offset-1 transition"
                  >
                    <Folder className="h-4 w-4 mr-3" />
                    Data Room
                  </div>
                </CollapsibleTrigger>
              </Collapsible>



              <Button
                className={`w-full text-left px-4 py-3 rounded-lg flex items-center justify-between disabled:cursor-not-allowed ${sidebarTab === "team-members"
                  ? "bg-blue-50 text-blue-700 font-medium"
                  : "hover:bg-gray-50 text-gray-600 hover:text-gray-800"
                  }`}
                onClick={() => {
                  setActiveSection("team-members");
                  setSidebarTab("team-members");
                }}
                disabled={!workflow.authorisedUser.enabled}
                tooltip={workflow.authorisedUser.reason}
                variant="textOnly"
              >
                <div className="flex items-center">
                  <UserRoundKey className="h-4 w-4 mr-3" />
                  Authorised Users
                </div>
                {!workflow.authorisedUser.enabled && <Lock className="h-4 w-4 text-gray-400" />}
              </Button>

            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 ml-[17%] mt-14 overflow-y-auto w-full ">
          {sidebarTab != "rental-calculator" &&
            sidebarTab != "data-room" &&
            sidebarTab != "login" &&
            sidebarTab != "purchase-order" &&
            sidebarTab != "purchase-order-pos" &&
            sidebarTab != "rental-schedule" &&
            sidebarTab != "notice-of-assignment" &&
            sidebarTab != "invoices" &&
            sidebarTab != "login-requirement-view" &&
            sidebarTab != "schedules-view" &&
            sidebarTab != "team-members"
            && (
              <div
                className="relative overflow-hidden rounded-xl"
                style={{ background: "linear-gradient(90deg, #0F6B73 0%, #1A8790 55%, #2A9AA3 100%)" }}
              >
                {(sessionEntityType !== 4 && sessionEntityType !== 5) && (
                  <div className="absolute right-3 top-3 z-30">
                    <button
                      onClick={toggleDropdown}
                      className="inline-flex items-center rounded-lg p-2 text-white/90 transition hover:bg-white/10"
                      type="button"
                      aria-label="More actions"
                    >
                      <svg
                        className="h-5 w-5"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 4 15"
                        fill="currentColor"
                      >
                        <path d="M3.5 1.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Zm0 6.041a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Zm0 5.959a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Z" />
                      </svg>
                    </button>

                    {isMenuOpen && (
                      <div
                        className="absolute right-0 z-50 mt-2 w-44 divide-y divide-gray-100 rounded-lg bg-white shadow-lg"
                        ref={dropdownRef}
                      >
                        <ul className="py-2 text-sm text-gray-700">
                          {isApiFetchedFlag(
                            customerData?.isComprehensiveApiFetched
                          ) ? (
                            <li className="w-full hover:cursor-pointer hover:bg-gray-100">
                              <button
                                onClick={() => setIsOnePagerOpen(true)}
                                className="flex w-full px-4 py-2 text-left text-gray-900 hover:bg-gray-100"
                              >
                                <Download className="mr-1 h-4 w-4" /> Download
                                CAM
                              </button>
                            </li>
                          ) : (
                            <li
                              className="w-full hover:cursor-pointer hover:bg-gray-100"
                              onClick={() => handleUnlockClick()}
                            >
                              <button className="flex w-full px-4 py-2 text-left text-gray-900 hover:bg-gray-100">
                                <LockKeyholeOpen className="mr-1 h-4 w-4" />{" "}
                                Unlock
                              </button>
                            </li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                <div className="relative z-10 flex min-h-[168px] items-stretch">
                  {/* Left: company info */}
                  <div className="relative flex min-w-0 flex-1 items-start gap-5 px-5 py-5 pr-8 md:pr-12">
                    <div className="relative flex h-[88px] w-[88px] shrink-0 items-center justify-center rounded-xl border border-white/30 bg-white shadow-sm">
                      {!CustomerDetails?.customerImage ? (
                        <div className="flex h-full items-center justify-center px-2 text-center text-xs text-slate-400">
                          No photo
                        </div>
                      ) : (
                        <img
                          src={CustomerDetails.customerImage}
                          className="h-full w-full rounded-xl object-cover"
                          alt="Customer"
                        />
                      )}
                      {isUploadingCustomerImage && (
                        <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-white/70">
                          <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#0F6B73] border-t-transparent" />
                        </div>
                      )}
                      <label className="absolute -bottom-2 -right-2 cursor-pointer rounded-full bg-white p-1.5 shadow-md">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          disabled={isUploadingCustomerImage}
                          onChange={(e) =>
                            onClickImageUpload(e, "CUSTOMER_ICON_IMAGE")
                          }
                        />
                        <Edit className="h-3.5 w-3.5 text-slate-700" />
                      </label>
                    </div>

                    <div className="min-w-0 flex-1 pt-0.5">
                      {(sessionEntityType !== 4 && sessionEntityType !== 5) && (
                        <>
                          <h1 className="mb-1 text-xl font-bold uppercase tracking-wide text-white md:text-2xl">
                            {customerData?.company_details?.company?.legal_name}
                          </h1>
                          {CustomerDetails?.brandName && (
                            <p className="mb-1 text-sm font-semibold text-white">
                              {CustomerDetails.brandName}
                            </p>
                          )}
                          <p className="mb-3 text-sm text-white/70">
                            ID:{" "}
                            {customerData?.company_details?.company?.cin ||
                              customerData.company_details.company.llpin}
                          </p>
                        </>
                      )}
                      {(sessionEntityType === 4 || sessionEntityType === 5) && (
                        <>
                          <h1 className="mb-1 text-xl font-bold uppercase tracking-wide text-white md:text-2xl">
                            {customerData?.company_details?.data?.data?.pnp?.legal_name}
                          </h1>
                          <p className="mb-3 text-sm text-white/70">
                            PAN:{" "}
                            {customerData?.company_details?.data?.data?.pnp?.pan}
                          </p>
                        </>
                      )}

                      <div className="mb-3 flex flex-nowrap items-center gap-x-3">
                        {(sessionEntityType !== 4 && sessionEntityType !== 5) && (
                          <>
                            <div className="flex shrink-0 items-center whitespace-nowrap text-sm text-white/90">
                              <Calendar className="mr-1.5 h-4 w-4 shrink-0" />
                              <span>
                                Founded:{" "}
                                {formatDate(
                                  customerData?.company_details?.company
                                    ?.incorporation_date
                                ) || "—"}
                              </span>
                            </div>
                            <div className="flex shrink-0 items-center whitespace-nowrap text-sm text-white/90">
                              <MapPin className="mr-1.5 h-4 w-4 shrink-0" />
                              <span>
                                {[
                                  customerData?.company_details?.company
                                    ?.registered_address?.state,
                                  "India",
                                ]
                                  .filter(Boolean)
                                  .join(", ")}
                              </span>
                            </div>
                            <div className="flex shrink-0 items-center gap-1.5 whitespace-nowrap text-sm">
                              {!showDomainRun ? (
                                <>
                                  <Globe className="h-4 w-4 shrink-0 text-white" />
                                  <a
                                    href={normalizeExternalUrl(resolvedBrandDomain)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sky-300 underline underline-offset-2 hover:text-sky-200"
                                  >
                                    Website
                                  </a>
                                </>
                              ) : isDomainEditorOpen || isFetchingBrandData ? (
                                <div className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-white/35 bg-white/10 px-2.5 py-1 backdrop-blur-[2px]">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      !isFetchingBrandData &&
                                      setIsDomainEditorOpen(false)
                                    }
                                    className="inline-flex shrink-0"
                                    title="Close"
                                  >
                                    <Globe className="h-4 w-4 text-white" />
                                  </button>
                                  <input
                                    autoFocus
                                    value={websiteDomainDraft}
                                    onChange={(e) =>
                                      setWebsiteDomainDraft(e.target.value)
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        e.preventDefault();
                                        void handleFetchCompanyByDomain();
                                      }
                                      if (e.key === "Escape") {
                                        e.preventDefault();
                                        setIsDomainEditorOpen(false);
                                      }
                                    }}
                                    placeholder="company.com"
                                    disabled={isFetchingBrandData}
                                    className="h-6 w-[160px] shrink-0 bg-transparent text-xs text-white outline-none placeholder:text-white/55 sm:w-[180px]"
                                  />
                                  <button
                                    type="button"
                                    onClick={() =>
                                      void handleFetchCompanyByDomain()
                                    }
                                    disabled={isFetchingBrandData}
                                    className="inline-flex h-6 min-w-[52px] shrink-0 items-center justify-center rounded-full bg-white px-2.5 text-[11px] font-semibold text-[#0F6B73] transition hover:bg-white/90 disabled:opacity-60"
                                  >
                                    {isFetchingBrandData ? (
                                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    ) : (
                                      "Run"
                                    )}
                                  </button>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setWebsiteDomainDraft(
                                      resolvedBrandDomain || ""
                                    );
                                    setIsDomainEditorOpen(true);
                                  }}
                                  className="inline-flex shrink-0 items-center text-white/90 transition hover:text-white"
                                  title="Enter domain to fetch company data"
                                >
                                  <Globe className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </>
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={checkAlert}
                          className="inline-flex items-center gap-1.5 rounded-md border border-white/50 bg-transparent px-2.5 py-1 text-xs font-semibold text-white transition hover:bg-white/10"
                        >
                          <span>MRA</span>
                          {isMra ? (
                            <Check className="h-3 w-3 text-emerald-300" />
                          ) : (
                            <X className="h-3 w-3 text-red-300" />
                          )}
                          <ExternalLink className="h-3 w-3 opacity-80" />
                        </button>
                        <button
                          type="button"
                          onClick={checkAlert}
                          className="inline-flex items-center gap-1.5 rounded-md border border-white/50 bg-transparent px-2.5 py-1 text-xs font-semibold text-white transition hover:bg-white/10"
                        >
                          <span>TS</span>
                          {isTermSheet ? (
                            <Check className="h-3 w-3 text-emerald-300" />
                          ) : (
                            <X className="h-3 w-3 text-red-300" />
                          )}
                          <ExternalLink className="h-3 w-3 opacity-80" />
                        </button>
                      </div>

                      {socialLinks.length > 0 && (
                        <div
                          className={`mt-3 inline-flex items-center gap-3 rounded-full border border-white/25 bg-black/70 px-3.5 py-1.5 shadow-sm backdrop-blur-[2px] ${
                            hasBannerImage ? "md:hidden" : ""
                          }`}
                        >
                          {socialLinks.map(({ key, label, href, type }) => (
                            <a
                              key={key}
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={label}
                              className="text-white no-underline transition hover:scale-110 hover:text-white"
                              style={{ color: "#ffffff" }}
                            >
                              <SocialIcon type={type} />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {hasBannerImage && (
                    <div
                      className="relative hidden w-[40%] min-w-[200px] shrink-0 overflow-hidden md:block"
                      style={{
                        clipPath: "polygon(14% 0, 100% 0, 100% 100%, 0 100%)",
                      }}
                    >
                      <div className="absolute inset-0 flex">
                        <img
                          src={bannerImages[0]}
                          alt="Company banner"
                          className="h-full w-full object-cover"
                        />
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-l from-black/35 via-black/10 to-transparent" />

                      {socialLinks.length > 0 && (
                        <div className="absolute bottom-3 right-4 z-10 inline-flex items-center gap-3 rounded-full border border-white/25 bg-black/70 px-3.5 py-1.5 shadow-sm backdrop-blur-[2px]">
                          {socialLinks.map(({ key, label, href, type }) => (
                            <a
                              key={key}
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={label}
                              className="text-white no-underline transition hover:scale-110 hover:text-white"
                              style={{ color: "#ffffff" }}
                            >
                              <SocialIcon type={type} />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

          {/* Content Area */}
          <div className="mt-4">
            {sidebarTab === "overview" && (
              <div className="space-y-6">
                <CompanyOverview
                  domain={brandDomain || websiteFromProfile || undefined}
                  companyName={
                    customerData?.company_details?.company?.legal_name ||
                    undefined
                  }
                  logoUrl={CustomerDetails?.customerImage || undefined}
                  apolloOrganization={CustomerDetails?.apolloOrganization}
                  useTestData={false}
                  customerId={customerId}
                  marketTicker={
                    (
                      customerData?.company_details?.company as
                        | {
                            ticker?: string;
                            stock_ticker?: string;
                          }
                        | undefined
                    )?.ticker ||
                    (
                      customerData?.company_details?.company as
                        | { stock_ticker?: string }
                        | undefined
                    )?.stock_ticker
                  }
                  marketExchange={
                    (
                      customerData?.company_details?.company as
                        | {
                            exchange?: string;
                            stock_exchange?: string;
                          }
                        | undefined
                    )?.exchange ||
                    (
                      customerData?.company_details?.company as
                        | { stock_exchange?: string }
                        | undefined
                    )?.stock_exchange
                  }
                  cin={
                    customerData?.company_details?.company?.cin ||
                    customerData?.company_details?.company?.llpin ||
                    undefined
                  }
                  status={
                    customerData?.company_details?.company?.status || undefined
                  }
                  leiNumber={
                    sessionEntityType === 4 || sessionEntityType === 5
                      ? customerData?.company_details?.data?.data?.pnp?.lei
                          ?.number
                      : customerData?.company_details?.company?.lei?.number
                  }
                  dataLastUpdated={
                    customerData?.company_details?.metadata?.last_updated ||
                    undefined
                  }
                  addresses={addresses || []}
                  openCharges={
                    customerData?.company_details?.open_charges || []
                  }
                  showGstinRegistrations={
                    sessionEntityType === 4 || sessionEntityType === 5
                  }
                  gstinBusinesses={businesses}
                  showCapitalInformation={
                    sessionEntityType !== 4 && sessionEntityType !== 5
                  }
                  capitalInformation={{
                    paidUpCapital:
                      customerData?.company_details?.company?.paid_up_capital ??
                      null,
                    totalContributionReceived:
                      customerData?.company_details?.company
                        ?.total_contribution_received ?? null,
                    authorizedCapital:
                      customerData?.company_details?.company
                        ?.authorized_capital ?? null,
                    totalObligationOfContribution:
                      customerData?.company_details?.company
                        ?.total_obligation_of_contribution ?? null,
                    sumOfCharges:
                      customerData?.company_details?.company?.sum_of_charges ??
                      null,
                  }}
                  aboutCompany={editedAboutCompany}
                  businessModel={editedBusinessModel}
                  canGenerateCompanySummary={companySummaryIsFetched === false}
                  isComprehensiveApiFetched={isApiFetchedFlag(
                    customerData?.isComprehensiveApiFetched
                  )}
                  isGeneratingCompanySummary={loading2}
                  isSavingCompanySummary={isSaving}
                  onAboutCompanyChange={(value) => {
                    setIsCompanySummaryTouched(true);
                    setEditedAboutCompany(value);
                  }}
                  onBusinessModelChange={(value) => {
                    setIsCompanySummaryTouched(true);
                    setEditedBusinessModel(value);
                  }}
                  onCompanySummaryBlur={handleBlur}
                  onGenerateCompanySummary={fetchgetCompanySummary}
                  onGenerateIndustrySummary={fetchIndustrySummary}
                  isGeneratingIndustrySummary={loading2}
                  canGenerateIndustrySummary={canGenerateIndustry}
                  industryOutlook={(
                    Array.isArray(companySummary?.industryNews)
                      ? companySummary.industryNews
                      : []
                  ).map((item: any, index: number) => ({
                    id: String(item?.url || item?.link || index),
                    title:
                      item?.name ||
                      item?.heading ||
                      item?.title ||
                      "Industry report",
                    summary: item?.snippet || "",
                    url: item?.url || item?.link || "#",
                  }))}
                  sectorName={
                    companySummary?.customerInfo?.industry ||
                    CustomerDetails?.industry ||
                    null
                  }
                  sectorOutlook={
                    companySummary?.sectorOutlook ??
                    companySummary?.industryOutlook ??
                    null
                  }
                  news={(
                    Array.isArray(companySummary?.news?.News_data)
                      ? companySummary.news.News_data
                      : []
                  ).map((item: any, index: number) => ({
                    id: String(item?.url || index),
                    title: item?.name || "News",
                    summary: item?.snippet || "",
                    url: item?.url || "#",
                  }))}
                  creditRatings={creditRatings}
                  creditEditable={{
                    brandName: CustomerDetails?.brandName || "",
                    creditRating: CustomerDetails?.creditRating || "",
                    creditRatingAgency: CustomerDetails?.creditRatingAgency || "",
                    turnoverYear: CustomerDetails?.turnoverYear || "",
                    turnover: CustomerDetails?.turnoverValue || "",
                    networthYear: CustomerDetails?.networthYear || "",
                    networth: CustomerDetails?.networthValue || "",
                  }}
                  onCreditSave={async (next) => {
                    try {
                      await apiService.post("/customers/updateCustomerField", {
                        customer_id: customerId,
                        brandName: next.brandName,
                        credit_rating: next.creditRating,
                        creditRatingAgency: next.creditRatingAgency,
                        turnoverYear: next.turnoverYear,
                        turnoverValue: next.turnover,
                        networthYear: next.networthYear,
                        networthValue: next.networth,
                      });
                      handlebrandNameChild(next.brandName);
                      handleCreditRatingChild(next.creditRating);
                      handleCreditRatingAgencyChild(next.creditRatingAgency);
                      handleTurnoverYearChild(next.turnoverYear);
                      handleTurnoverValueChild(next.turnover);
                      handleNetworthYearChild(next.networthYear);
                      handleNetworthValueChild(next.networth);
                      toast.success("Credit inputs updated");
                    } catch {
                      toast.error("Failed to update credit inputs");
                    }
                  }}
                  onManageAddresses={() => setAddressManagementOpen(true)}
                  formatCurrency={formatCurrency}
                  getAddressTypeLabel={getAddressTypeLabel}
                  getStateName={(stateId) =>
                    indiaState.find(
                      (state: any) => String(state.id) === String(stateId)
                    )?.state_name || String(stateId || "")
                  }
                  financialScoreSlot={(() => {
                    const probeScore =
                      customerData?.company_details?.probe_financial_score;
                    const score =
                      Array.isArray(probeScore) && probeScore.length > 0
                        ? probeScore[0]
                        : !Array.isArray(probeScore) &&
                            probeScore &&
                            Object.keys(probeScore).length > 0
                          ? probeScore
                          : null;
                    if (!score) return null;
                    return (
                      <div className="rounded-lg border border-green-200 p-4">
                        <p className="mb-3 text-sm font-semibold text-green-700">
                          Financial Score
                        </p>
                        <RiskGradeSection riskGrade={score as any} />
                      </div>
                    );
                  })()}
                  financialPerformanceSlot={
                    overviewFinancialChartData.length > 0 ? (
                      <FinancialDashboard
                        financialDatas={overviewFinancialChartData}
                      />
                    ) : null
                  }
                />

              </div>
            )}

            {sidebarTab === "director" && (
              <div>
                <DirectorDetails
                  director={customerData.director?.authorized_signatories || []}
                  directorNetworks={
                    customerData.director?.director_network || []
                  }
                  customerId={customerId}
                />
              </div>
            )}

            {sidebarTab === "compliance" && (
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h2 className="text-xl font-bold mb-4">Compliance</h2>
                <div className="border-b border-gray-200 mb-6">
                  <nav className="flex space-x-6">
                    <button
                      className={`pb-3 ${complianceTab === "gst"
                        ? "text-blue-600 border-b-2 border-blue-600 font-medium"
                        : "text-gray-500"
                        }`}
                      onClick={() => setComplianceTab("gst")}
                    >
                      GST
                    </button>
                    <button
                      className={`pb-3 ${complianceTab === "epfo"
                        ? "text-blue-600 border-b-2 border-blue-600 font-medium"
                        : "text-gray-500"
                        }`}
                      onClick={() => setComplianceTab("epfo")}
                    >
                      EPFO
                    </button>
                  </nav>
                </div>
                {complianceTab === "gst" && (
                  <GSTComplianceSection
                    gstDetails={customerData?.gstEpfo?.gst_details || []}
                  />
                )}
                {complianceTab === "epfo" && (
                  <EpfoTab
                    establishments={
                      customerData?.gstEpfo
                        ?.establishments_registered_with_epfo || []
                    }
                  />
                )}
              </div>
            )}

            {sidebarTab === "legal" && (
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <LegalTab
                  legalCases={customerData.company_details.legal_history}
                  customerId={customerId}
                />
              </div>
            )}
            {sidebarTab === "peer-comparison" && (
              <div className="bg-white rounded-lg shadow-sm">
                <PeerComparison
                  financialData={customerData.peerComparison}
                  customerId={customerId}
                />
              </div>
            )}

            {sidebarTab === "financial" && (
              <div>
                <FinancialTab
                  financials={customerData?.financial?.financials}
                  financialupload={
                    handlefinancialupload as (
                      payload: FinancialData[],
                      type: string,
                      nature: string
                    ) => void
                  }
                  customerId={customerId || ""}
                  ratioActionButton={ratioActionButton}
                  financialrefresh={
                    handlefinancialrefresh as () => Promise<void>
                  }
                />
              </div>
            )}

            {sidebarTab === "related-parties" && (
              <div>
                <RelatedParty
                  holdingEntities={
                    customerData.company_details.holding_entities || null
                  }
                  subsidiaryEntities={
                    customerData.company_details.subsidiary_entities || null
                  }
                  associateEntities={
                    customerData.company_details.associate_entities || null
                  }
                  jointVentures={
                    customerData.company_details.joint_ventures || null
                  }
                  relatedPartyTransactions={
                    customerData.company_details.related_party_transactions ||
                    null
                  }
                />
              </div>
            )}

            {sidebarTab === "shareholding" && (
              <div>
                <ShareholdingPattern
                  shareholdings={customerData?.director?.shareholdings || []}
                  directorShareholdings={
                    customerData?.director?.director_shareholdings || []
                  }
                  shareholdingsMoreThanFivePercent={
                    customerData?.director
                      ?.shareholdings_more_than_five_percent || []
                  }
                />
              </div>
            )}

            {sidebarTab === "llp-contribution" && (
              <div>
                <LLPContributionDetails
                  contributionDetails={
                    (customerData?.company_details as any)?.contribution_details
                  }
                />
              </div>
            )}

            {sidebarTab === "securities-allotment" && (
              <div>
                <SecuritiesAllotment
                  securities={
                    customerData.company_details.securities_allotment || []
                  }
                />
              </div>
            )}

            {sidebarTab === "debt-profile" && (
              <div>
                <DebtProfile />
              </div>
            )}

            {sidebarTab === "credit-notes" && (
              <div>
                <CreditNotes customerId={customerId} />
              </div>
            )}

            {sidebarTab === "rental-calculator" && (
              <div>
                <RentalCalculatorLayout customerId={customerId} />
              </div>
            )}

            {(sidebarTab === "rental-schedule" || sidebarTab === "schedules" || sidebarTab === "notice-of-assignment" || sidebarTab === "invoices" || sidebarTab === "schedules-view") && (
              <div>
                <RentalSchedulesPage initialTab={sidebarTab === "rental-schedule" ? "schedules" : sidebarTab} scheduleKey={rentalScheduleKey || 0} scheduleData={schedule} />
              </div>
            )}

            {sidebarTab === "data-room" && (
              <div className="bg-white  rounded-lg shadow-sm">
                <DataRoom />
              </div>
            )}

            {sidebarTab === "login" && (
              <div className="bg-white  rounded-lg shadow-sm">
                <Login handleViewRequirement={handleViewRequirement} adminUserList={adminUserList} />
              </div>
            )}

            {sidebarTab === "login-requirement-view" && (
              <div className="bg-white  rounded-lg shadow-sm">
                <RequirementDetail
                  requirementData={requirementData}
                  adminUserList={adminUserList}
                  onBack={() => {
                    setSidebarTab("login");
                    setRequirementData(null);
                  }}
                />
              </div>
            )}

            {sidebarTab === "purchase-order" && (
              <div className="bg-white p-2 rounded-lg shadow-sm">
                <PurchaseOrders
                  handleViewPos={handleViewPos}
                  indiaState={indiaState}
                  customers={companySummary?.customerInfo || {}}
                />
              </div>
            )}
            {sidebarTab === "purchase-order-pos" && (
              <div className="bg-white p-2 rounded-lg shadow-sm">
                <PODetail
                  poDetails={poDetails as any}
                  adminUserList={adminUserList}
                  indiaState={indiaState}
                  onClose={() => setSidebarTab("purchase-order")}
                />
              </div>
            )}
            {sidebarTab === "team-members" && (
              <div className="bg-white p-2 rounded-lg shadow-sm p-5">
                <TeamMembersContent isActive={sidebarTab === "team-members"} />
              </div>
            )}
          </div>
        </main>

      </div>

      {isOnePagerOpen && (
        <OnePagerModal
          isOpen={isOnePagerOpen}
          onClose={() => setIsOnePagerOpen(false)}
          CustomerData={onePageModelData || {}}
        />
      )}

      <DataUpdatePopup
        isOpen={isDataUpdatePopupOpen}
        onClose={() => setIsDataUpdatePopupOpen(false)}
        companyName={customerData?.company_details?.company?.legal_name || ""}
        companyId={customerId || ""}
        handleUnlock={handleFetchCompanyComprehensiveSummary}
      />

      <CustomerAddressManagementDialog
        open={addressManagementOpen}
        add={addaddressManagementOpen}
        onOpenChange={handleAddressDialog}
        addresses={addresses || []}
        onUpdate={handleUpdateAddresses}
        indianStates={indiaState}
        customerId={customerId || ""}
        sessionEntityType={sessionEntityType}
      // editAddress={editAddressId}
      />

      <CustomerDocumentsSheet
        open={mraOpen}
        onOpenChange={() => { setMRAOpen(false) }}
        customerId={customerId}
        customerName={customerData?.company_details?.company?.legal_name || ""}
        onUpload={getDocUploads}
        onDelete={() => { }}
      />
    </TooltipProvider>
  );

});


export default CustomerProfile;
